import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import { Box, Flex } from '@chakra-ui/react';
import styled from 'styled-components';
import { Scrollbar } from 'react-scrollbars-custom';
import breakpoints from '../../../breakpoints';
import PrevNextBtn from '../../atoms/PrevNextBtn';

const StyledSimpleSlider = styled(Box)`
  .ScrollbarsCustom-Track {
    right: 0px;
    left: inherit !important;
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(226, 229, 233, 1) 50%,
      rgba(255, 255, 255, 1) 100%
    ) !important;
    height: 5px !important;

    @media (max-width: ${breakpoints.sm}) {
      width: 75% !important;
    }
  }

  .ScrollbarsCustom-Thumb {
    background: #a9b0bc !important;
  }

  .ScrollbarsCustom-Wrapper {
    width: calc(100% + 24px);

    @media (min-width: ${breakpoints.md}) {
      width: calc(100% + 40px);
    }

    @media (min-width: ${breakpoints.lg}) {
      width: calc(100% + 80px);
    }

    @media (min-width: ${breakpoints['2xl']}) {
      width: 100%;
    }
  }
`;
function SimpleSlider({
  items,
  sliderItem,
  setIsFinalScroll = () => {},
  ...props
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [childrenHeight, setChildrenHeight] = useState(0);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const containerRef = useRef(null);
  const scrollRef = useRef(null);

  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const scroll = scrollRef?.current;

    const handleResize = () => {
      setIsScrollbarVisible(container?.scrollWidth > container?.clientWidth);

      const firstChildHeight = scroll?.offsetHeight;

      if (!firstChildHeight) return;

      setChildrenHeight(firstChildHeight + 20);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scrollRef?.current?.offsetHeight, items]);

  const itemsWithIDs = useMemo(
    () =>
      items?.filter(Boolean).map(item => {
        const id = uniqueId(item._key || item._id);
        return { ...item, id };
      }) || [],
    [items]
  );

  const handleKeyDown = event => {
    const container = containerRef.current;
    if (event.key === 'ArrowRight' || event === 'right') {
      container.scrollLeft += 100;
    } else if (event.key === 'ArrowLeft' || event === 'left') {
      container.scrollLeft -= 100;
    }
  };

  const handleMouseDown = event => {
    event.preventDefault();
    setIsDragging(true);
    startXRef.current = event.clientX;
    scrollLeftRef.current = containerRef.current.scrollLeft;
  };

  const handleMouseMove = event => {
    if (!isDragging) return;
    event.preventDefault();
    const x = event.clientX;
    const scroll = x - startXRef.current;
    containerRef.current.scrollLeft = scrollLeftRef.current - scroll;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    const isScrollAtEnd =
      container.scrollLeft + container.clientWidth === container.scrollWidth;

    setIsFinalScroll(isScrollAtEnd);
  };

  if (!items) return null;
  if (!sliderItem) return null;

  return (
    <StyledSimpleSlider mb={8} {...props}>
      <Box
        as={Scrollbar}
        onClick={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        ref={containerRef}
        onScroll={handleScroll} // Use onScroll prop here
        minH={childrenHeight + 30}
      >
        <Flex
          ref={scrollRef}
          gap={[2, 6]}
          onKeyDown={handleKeyDown}
          tabIndex="0"
        >
          {itemsWithIDs?.map((item, index) => (
            <Box
              key={item.id}
              pr={
                index === itemsWithIDs.length - 1
                  ? { base: '24px', md: '40px', lg: '80px', '2xl': '120px' }
                  : '0'
              }
            >
              {sliderItem(item)}
            </Box>
          ))}
        </Flex>
      </Box>
      {isScrollbarVisible && (
        <PrevNextBtn
          onScrollLeft={() => handleKeyDown('left')}
          onScrollRight={() => handleKeyDown('right')}
          contentKey="simple_slider"
        />
      )}
    </StyledSimpleSlider>
  );
}

SimpleSlider.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      _key: PropTypes.string,
      _id: PropTypes.string,
    })
  ).isRequired,
  sliderItem: PropTypes.func.isRequired,
}.isRequired;

export default SimpleSlider;
